import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import styled from 'styled-components';
import classnames from 'classnames';
import TextareaAutosize from 'react-autosize-textarea';
import theme from '../theme';
import fn from '../../helpers/fn';
import Message from '../text/Message';
import Label from '../text/Label';
import ActionLinkButton from '../links/ActionLinkButton';
import Loader from '../icons/Loader';
import ValidIcon from '../icons/ValidIcon';
import FadeIn from '../blocks/FadeIn';

const isBrowser = typeof window !== 'undefined';
const AceEditor = isBrowser ? require('react-ace').default : null; // eslint-disable-line
const githubTheme = isBrowser ? require('brace/theme/github') : null; // eslint-disable-line
const jsonMode = isBrowser ? require('brace/mode/json') : null; // eslint-disable-line

const InputFieldContainer = styled.div`
  position: relative;
  width: 100%;

  margin: 0 ${theme.unit / 2}px;
  &:first-child {
    margin-left: 0;
  }

  &:last-child {
    margin-right: 0;
  }
`;

class Input extends React.PureComponent {
  static propTypes = {
    input: PropTypes.shape({
      onChange: PropTypes.func,
    }).isRequired,
    meta: PropTypes.shape({
      error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.object]),
      valid: PropTypes.bool,
      asyncValidating: PropTypes.bool,
    }),
    label: PropTypes.string,
    className: PropTypes.string,
    labelType: PropTypes.oneOf(['default', 'floating']),
    showLabel: PropTypes.bool,
    inverse: PropTypes.bool,
    type: PropTypes.string,
    showLoading: PropTypes.bool,
    showValid: PropTypes.bool,
    style: PropTypes.object,
    refs: PropTypes.func,
    loaderStyle: PropTypes.object,
    validStyle: PropTypes.object,
    loaderProps: PropTypes.object,
    validProps: PropTypes.object,
    containerStyle: PropTypes.object,
  };

  static defaultProps = {
    label: null,
    className: null,
    labelType: 'default',
    showLabel: undefined,
    inverse: false,
    type: 'text',
    showLoading: false,
    showValid: false,
    style: null,
    refs: () => null,
    meta: {},
    loaderStyle: null,
    validStyle: null,
    loaderProps: null,
    validProps: null,
    containerStyle: null,
  };

  state = {
    showPassword: false,
  }

  render() {
    const {
      meta,
      input,
      label,
      labelType,
      showLabel = !!label,
      className,
      inverse,
      type: originalType,
      showLoading,
      showValid,
      style,
      refs,
      loaderStyle,
      validStyle,
      loaderProps,
      validProps,
      auto, // eslint-disable-line
      containerStyle,
      ...props
    } = this.props;
    const {
      showPassword,
    } = this.state;

    let type = originalType;
    if (originalType === 'password') {
      type = showPassword ? 'text' : 'password';
    }

    return (
      <InputFieldContainer style={containerStyle}>
        {(() => {
          switch (type) {
            case 'textarea':
              return (
                <TextareaAutosize
                  ref={ref => { this.input = ref; refs(ref); }}
                  {...input}
                  {...props}
                  className={classnames(className, {
                    empty: !input.value,
                  })}
                  style={{
                    lineHeight: '20px',
                    boxSizing: 'border-box',
                    paddingTop: 13,
                    paddingBottom: 5,
                    minHeight: 48,
                    resize: 'none',
                    display: 'block',
                    ...meta.touched && meta.error && !inverse && {
                      borderColor: theme.colors.black,
                    },
                    ...meta.touched && meta.error && {
                      marginBottom: -1,
                    },
                    ...meta.active && !inverse && {
                      borderColor: theme.colors.active,
                    },
                    ...labelType === 'floating' && !!input.value && {
                      paddingTop: 20,
                      paddingBottom: 4,
                    },
                    ...style,
                  }}
                />
              );
            case 'code':
              return (
                <AceEditor
                  ref={ref => { this.input = ref; refs(ref); }}
                  wrapEnabled
                  theme="github"
                  tabSize={2}
                  editorProps={{ $blockScrolling: true }}
                  width="100%"
                  setOptions={{
                    showInvisibles: false,
                    showPrintMargin: false,
                    showLineNumbers: true,
                    displayIndentGuides: true,
                    highlightGutterLine: false,
                  }}
                  {...props}
                  value={input.value}
                  {...input.onChange && { onChange: input.onChange }}
                  className={classnames(className, {
                    empty: !input.value,
                  })}
                  style={{
                    lineHeight: '20px', // input.value ? '24px' : '32px',
                    boxSizing: 'border-box',
                    paddingTop: 13,
                    paddingBottom: 5,
                    minHeight: 48,
                    fontSize: 12,
                    resize: 'none',
                    display: 'block',
                    // paddingBottom: 4,
                    ...meta.touched && meta.error && !inverse && {
                      borderColor: theme.colors.black,
                    },
                    ...meta.touched && meta.error && {
                      marginBottom: -1,
                    },
                    ...meta.active && !inverse && {
                      borderColor: theme.colors.active,
                    },
                    ...labelType === 'floating' && !!input.value && {
                      paddingTop: 20,
                      paddingBottom: 4,
                    },
                    ...style,
                  }}
                />
              );
            default:
              return (
                <input
                  ref={ref => { this.input = ref; refs(ref); }}
                  type={type === 'search' ? // eslint-disable-line
                    (showLoading && meta && meta.asyncValidating ? 'text' : 'search')
                    : type.replace('-confirm', '')}
                  {...input}
                  {...props}
                  className={classnames(className, {
                    empty: !input.value,
                  })}
                  style={{
                    ...meta.touched && meta.error && !inverse && {
                      borderColor: theme.colors.black,
                    },
                    ...meta.touched && meta.error && typeof meta.error === 'string' && {
                      marginBottom: -1,
                    },
                    ...meta.active && !inverse && {
                      borderColor: theme.colors.active,
                    },
                    ...labelType === 'floating' && !!input.value && {
                      paddingTop: 24,
                    },
                    ...style,
                  }}
                />
              );
          }
        })()}
        {showLabel && label && (
          <Label small style={{ pointerEvents: 'none' }}>{label}</Label>
        )}
        {labelType === 'floating' && input.value.length > 0 && originalType === 'password' && (
          <ActionLinkButton
            right
            type="button"
            tabIndex={-1}
            style={{
              position: 'absolute',
              top: 0,
              height: 46,
              lineHeight: '46px',
              right: 12,
              zIndex: 1,
            }}
            onClick={() => this.setState(() => ({
              showPassword: !showPassword,
            }))}
          >{showPassword ? 'Hide' : 'Show'}</ActionLinkButton>
        )}
        {meta.touched && meta.error && typeof meta.error === 'string' && (
          <Message
            warning
            key="1"
          >
            {meta.error}
          </Message>
        )}
        {showLoading && meta && meta.asyncValidating && (
          <FadeIn>
            <Loader
              size={16}
              style={{
                position: 'absolute',
                top: 15,
                right: 12,
                zIndex: 1,
                ...loaderStyle,
              }}
              {...loaderProps}
            />
          </FadeIn>
        )}
        {showValid
          && meta
          && meta.valid
          && !meta.error
          && !meta.asyncValidating && (
            <ValidIcon
              style={{
                position: 'absolute',
                top: 12,
                right: 6,
                zIndex: 1,
                ...validStyle,
              }}
              {...validProps}
            />
          )}
      </InputFieldContainer>
    );
  }
}

const InputField = props => (props.name ? (
  <Field
    component={Input}
    {...props}
  />
) : <Input {...props} />);
InputField.propTypes = {
  name: PropTypes.string,
};
InputField.defaultProps = {
  name: null,
};

export default fn(__filename, styled.input`
  appearance: none;
  height: ${6 * theme.unit}px;
  line-height: ${6 * theme.unit}px;
  background: ${theme.colors.white};
  font-size: inherit;
  padding: ${theme.unit}px ${theme.unit * 1.5}px;
  width: 100%;
  box-sizing: border-box;
  border: solid 1px ${theme.colors.gray16};
  border-radius: ${theme.unit}px;
  outline: 0;
  transition: padding-top .3s ease 0s;

  & + label {
    opacity: 1;
    transition: opacity .3s ease 0s;
    color: inherit;
  }

  &.empty + label {
    opacity: 0;
  }

  &[disabled] {
    background: ${theme.colors.gray19};
  }

  &[disabled] + label {
    color: ${theme.colors.gray4};
  }

  ${({ inverse }) => inverse && `
    border: solid 1px ${theme.colors.white};
    background: ${theme.colors.whiteColor.fade(0.9)};
    color: ${theme.colors.white};

    & + label {
      color: ${theme.colors.whiteColor.fade(0.5)};
    }

    &:-webkit-autofill + label {
      color: black;
    }

    &::-webkit-input-placeholder {
      color: ${theme.colors.whiteColor.fade(0.5)};
    }
  `}
`.withComponent(({
  inverse, // strip
  ...props
}) => <InputField {...props} />));



// WEBPACK FOOTER //
// ./src/components/fields/InputField.js